<template>
    <div class="addon" :class="[padding && 'padding']">
        <div class="addon-inner">
            <SolidLogo v-if="showSolidLogo" placement="checkout" />

            <div v-if="illustration" class="addon-asset">
                <Illustration :data="illustration" />
            </div>

            <div class="addon-content">
                <p v-if="title" class="addon-content-title">
                    {{ title }}

                    <span v-if="price && !isIncluded" class="addon-content-price">
                        {{ getMoneyObjectAsString(price) }}
                    </span>

                    <Badge
                        v-if="isIncluded"
                        color="green"
                        size="s"
                        iconPosition="right"
                        :text="
                            t({
                                key: 'checkout.included',
                                format: LabelFormat.Capitalize,
                            })
                        "
                        :icon="includedTooltip ? 'InfoCircle' : undefined"
                        :iconTooltip="includedTooltip" />
                </p>
                <p v-if="description" class="addon-content-description">
                    {{ description }}
                </p>

                <ul v-if="rows.length > 0" class="addon-content-rows">
                    <li v-for="(row, index) in rows" :key="index">
                        <Icon type="Check" color="green" /> {{ row }}
                    </li>
                </ul>

                <div class="addon-content-links">
                    <slot name="readmorelinks" />
                </div>

                <slot name="content" />
            </div>
        </div>

        <div v-if="!isIncluded" class="addon-footer">
            <div class="addon-footer-content" :class="{ error: hasErrors }">
                <div class="addon-footer-errors">
                    <Badge
                        v-if="hasErrors"
                        :text="errors[0]"
                        size="m"
                        color="red"
                        iconPosition="right" />
                </div>
            </div>
            <div class="addon-footer-actions">
                <ButtonPrimary v-if="modelValue === false" icon="Check">
                    {{ t({ key: 'shared.no' }) }}
                </ButtonPrimary>
                <ButtonSecondary v-else :onClick="() => onSelect(false)">
                    {{ t({ key: 'shared.no' }) }}
                </ButtonSecondary>

                <ButtonPrimary v-if="modelValue" icon="Check">
                    {{ t({ key: 'shared.yes_thank_you' }) }}
                </ButtonPrimary>
                <ButtonSecondary v-else :onClick="() => onSelect(true)">
                    {{ t({ key: 'shared.yes_thank_you' }) }}
                </ButtonSecondary>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType, useSlots } from 'vue';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { Money } from '@libs/money/money.interfaces';
    import { IllustrationData } from '@ui/components/Illustration/Illustration.interfaces';
    import { LabelFormat } from '@shared/interfaces/translation';
    import Icon from '@ui/components/Icon/Icon.vue';
    import Badge from '@ui/components//Badge/Badge.vue';
    import ButtonSecondary from '../Button/ButtonSecondary.vue';
    import ButtonPrimary from '../Button/ButtonPrimary.vue';
    import Illustration from '../Illustration/Illustration.vue';
    import SolidLogo from '@/components/shared/SolidLogo/SolidLogo.vue';

    export default defineComponent({
        name: 'Addon',
        methods: { t, getMoneyObjectAsString },
        components: {
            Icon,
            Illustration,
            ButtonSecondary,
            ButtonPrimary,
            Badge,
            SolidLogo,
        },
        emits: ['update:modelValue'],
        props: {
            modelValue: {
                type: Boolean as PropType<boolean | undefined>,
                default: undefined,
            },
            title: String,
            description: String,
            price: Object as PropType<Money>,
            illustration: { type: Object as PropType<IllustrationData> },
            padding: { type: Boolean, default: true },
            isIncluded: { type: Boolean, default: false },
            includedTooltip: String,
            rows: {
                type: Array as PropType<string[]>,
                default: () => [],
            },
            errors: {
                type: Array as PropType<string[]>,
                default: () => [],
            },
            showSolidLogo: { type: Boolean, default: false },
            onAccept: Function,
            onDeny: Function,
        },
        setup(props, { emit }) {
            const slots = useSlots();

            const hasErrors = computed(() => props.errors.length > 0);

            function onSelect(state: boolean) {
                emit('update:modelValue', state);
            }

            return {
                slots,
                onSelect,
                hasErrors,
                LabelFormat,
            };
        },
    });
</script>
<style lang="scss" scoped>
    @forward './Addon.scss';
</style>
